import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { myPagePath } from '../utils/myPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordInput from '../components/PasswordInput';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const Account = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState({
    first_name: '',
    email: ''
  });
  const [isProfileChanged, setIsProfileChanged] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [handle, setHandle] = useState('');
  const [handleStatus, setHandleStatus] = useState({ state: 'idle', reason: null });
  const [isSavingHandle, setIsSavingHandle] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [isOpeningBilling, setIsOpeningBilling] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/');
      return;
    }
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    setProfileData({
      first_name: parsedUser.first_name || '',
      email: parsedUser.email || ''
    });
    setHandle(parsedUser.username || '');

    // The username may be missing/stale in localStorage — refresh it from the server.
    const token = localStorage.getItem('adminToken');
    if (token) {
      axios.get(`${API}/api/my-profile`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          if (res.data?.username) {
            setHandle(res.data.username);
            const merged = { ...parsedUser, username: res.data.username };
            localStorage.setItem('user', JSON.stringify(merged));
            setUser(merged);
          }
        })
        .catch(() => {});
      axios.get(`${API}/api/subscription`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setSubscription(res.data || { status: 'none' }))
        .catch(() => setSubscription({ status: 'none' }));
    }
  }, [navigate]);

  const USERNAME_RE = /^[a-z0-9_](?:[a-z0-9_-]{1,28}[a-z0-9_])?$/;

  // Debounced live "is this handle free?" check while typing (same as signup).
  useEffect(() => {
    const candidate = (handle || '').trim().toLowerCase();
    if (!candidate || candidate === (user?.username || '')) {
      setHandleStatus({ state: 'idle', reason: null });
      return;
    }
    if (candidate.length < 3 || candidate.length > 30 || !USERNAME_RE.test(candidate)) {
      setHandleStatus({
        state: 'invalid',
        reason: 'Use 3-30 lowercase letters, numbers, underscores, or hyphens',
      });
      return;
    }
    setHandleStatus({ state: 'checking', reason: null });
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/username-available`, { params: { username: candidate } });
        if (cancelled) return;
        if (res.data.available) {
          setHandleStatus({ state: 'available', reason: null });
        } else {
          setHandleStatus({ state: 'taken', reason: res.data.reason || 'That username is already taken.' });
        }
      } catch {
        if (!cancelled) setHandleStatus({ state: 'idle', reason: null });
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [handle, user?.username]);

  const handleSaveHandle = async (e) => {
    e.preventDefault();
    const candidate = (handle || '').trim().toLowerCase();
    if (!candidate || candidate === (user?.username || '')) return;
    if (handleStatus.state === 'invalid' || handleStatus.state === 'taken') {
      toast.error(handleStatus.reason || 'Please choose a different username');
      return;
    }
    setIsSavingHandle(true);
    try {
      const token = localStorage.getItem('adminToken');
      // Send only the username; the backend only renames when the handle
      // explicitly differs from the current one.
      const response = await axios.put(
        `${API}/api/profile`,
        { username: candidate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newUsername = response.data?.username || candidate;
      const updatedUser = { ...user, username: newUsername };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setHandle(newUsername);
      setHandleStatus({ state: 'idle', reason: null });
      toast.success(`Username updated! Your page is now getwellby.com/${newUsername}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update username';
      toast.error(errorMsg);
    } finally {
      setIsSavingHandle(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    setIsProfileChanged(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    if (!profileData.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    
    if (!profileData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(
        `${API}/api/profile`,
        {
          first_name: profileData.first_name,
          email: profileData.email
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update local storage with new data
      const updatedUser = { ...user, ...profileData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setIsProfileChanged(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update profile';
      toast.error(errorMsg);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!passwordData.newPassword || !passwordData.confirmPassword || !passwordData.currentPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API}/api/auth/change-password`,
        {
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Password changed successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMsg);
    }
  };

  const handleManageBilling = async () => {
    setIsOpeningBilling(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API}/api/customer-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.portal_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Could not open billing management';
      toast.error(errorMsg);
    } finally {
      setIsOpeningBilling(false);
    }
  };

  const subscriptionStatus = subscription?.status;
  const trialEnd = subscription?.trial_end
    ? new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      .format(new Date(subscription.trial_end))
    : null;

  const planDescription = subscriptionStatus === 'active'
    ? 'You’re on the Home Cook plan. View receipts, update your payment method, or cancel your subscription.'
    : subscriptionStatus === 'trialing'
      ? `You’re enjoying the Home Cook free trial${trialEnd ? ` through ${trialEnd}` : ''}.`
      : subscriptionStatus === 'expired'
        ? 'Your Home Cook free trial has ended. Choose a plan to keep creating and editing recipes.'
        : 'Choose Home Cook to save, share, and organize your recipes.';

  const handleCancelAccount = async () => {
    if (cancelConfirmText !== 'CANCEL') return;
    
    setIsCancelling(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/api/auth/cancel-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      localStorage.removeItem('adminToken');
      localStorage.removeItem('cachedCategories');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      toast.success('Your account has been cancelled.');
      navigate('/');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to cancel account';
      toast.error(errorMsg);
    } finally {
      setIsCancelling(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EEE7D8' }}>
      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back Link */}
        <button 
          onClick={() => navigate(myPagePath())} 
          className="mb-6 hover:underline transition-none"
          style={{ color: '#875C66', fontFamily: "'Bricolage Grotesque', sans-serif", background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          ← View All Recipes
        </button>

        <h1 
          className="text-4xl font-bold mb-8"
          style={{ 
            fontFamily: "'Instrument Serif', serif", 
            color: '#51120A' 
          }}
        >
          Account Settings
        </h1>

        {/* Account Information */}
        <Card className="mb-6" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <CardHeader>
            <CardTitle style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
              Account Information
            </CardTitle>
            <CardDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              Your basic account details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div>
                <Label style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
                  First Name
                </Label>
                <Input
                  value={profileData.first_name}
                  onChange={(e) => handleProfileChange('first_name', e.target.value)}
                  style={{ 
                    borderColor: '#D6D0C2', 
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    backgroundColor: 'transparent'
                  }}
                />
              </div>
              <div>
                <Label style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
                  Email
                </Label>
                <Input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  style={{ 
                    borderColor: '#D6D0C2', 
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    backgroundColor: 'transparent'
                  }}
                />
              </div>
              {isProfileChanged && (
                <Button
                  type="submit"
                  style={{ 
                    backgroundColor: '#51120A', 
                    color: 'white', 
                    fontFamily: "'Bricolage Grotesque', sans-serif" 
                  }}
                >
                  Save Changes
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        {subscription && (
          <Card className="mb-6" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
            <CardHeader>
              <CardTitle style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
                Plan
              </CardTitle>
              <CardDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A', opacity: 0.8 }}>
                {planDescription}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {subscriptionStatus === 'active' ? (
                <button
                  type="button"
                  onClick={handleManageBilling}
                  disabled={isOpeningBilling}
                  className="text-sm underline hover:opacity-70 disabled:opacity-50"
                  style={{
                    color: '#51120A',
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    background: 'none',
                    border: 'none',
                    cursor: isOpeningBilling ? 'default' : 'pointer',
                    padding: 0,
                  }}
                >
                  {isOpeningBilling ? 'Opening billing…' : 'Manage billing'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/pricing')}
                  className="text-sm underline hover:opacity-70"
                  style={{
                    color: '#51120A',
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                >
                  View plans
                </button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Handle */}
        <Card className="mb-6" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <CardHeader>
            <CardTitle style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
              Username
            </CardTitle>
            <CardDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              Your public page link: getwellby.com/{user.username || 'yourusername'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveHandle} className="space-y-4">
              <div>
                <Label htmlFor="handle" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
                  Username
                </Label>
                <Input
                  id="handle"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                  placeholder="yourusername"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-testid="account-handle-input"
                  style={{
                    borderColor: (handleStatus.state === 'invalid' || handleStatus.state === 'taken') ? '#DC2626' : '#D6D0C2',
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    backgroundColor: 'transparent'
                  }}
                />
                <p className="text-xs mt-1" data-testid="account-handle-hint" style={{
                  color: (handleStatus.state === 'invalid' || handleStatus.state === 'taken') ? '#DC2626' : '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  opacity: (handleStatus.state === 'invalid' || handleStatus.state === 'taken') ? 1 : 0.7
                }}>
                  {handleStatus.state === 'invalid' || handleStatus.state === 'taken'
                    ? handleStatus.reason
                    : handleStatus.state === 'checking'
                      ? 'Checking availability...'
                      : handleStatus.state === 'available'
                        ? `Available — your page will be getwellby.com/${handle.trim().toLowerCase()}`
                        : ''}
                </p>
              </div>
              {handle.trim().toLowerCase() !== (user.username || '') && handle.trim() && (
                <>
                  <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A', fontSize: '0.85rem', backgroundColor: '#EEE7D8', border: '1px solid #D6D0C2', borderRadius: '6px', padding: '10px 12px' }} data-testid="account-handle-warning">
                    <strong>Heads up:</strong> your page link will change to the new username. Links you've already shared (getwellby.com/{user.username}) will automatically forward to your new page.
                  </p>
                  <Button
                    type="submit"
                    disabled={isSavingHandle || handleStatus.state === 'invalid' || handleStatus.state === 'taken' || handleStatus.state === 'checking'}
                    data-testid="account-handle-save"
                    style={{
                      backgroundColor: '#51120A',
                      color: 'white',
                      fontFamily: "'Bricolage Grotesque', sans-serif"
                    }}
                  >
                    {isSavingHandle ? 'Saving...' : 'Save Username'}
                  </Button>
                </>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <CardHeader>
            <CardTitle style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
              Change Password
            </CardTitle>
            <CardDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              Update your password to keep your account secure
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="currentPassword" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
                  Current Password
                </Label>
                <PasswordInput
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                  style={{ borderColor: '#D6D0C2', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                />
              </div>
              <div>
                <Label htmlFor="newPassword" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
                  New Password
                </Label>
                <PasswordInput
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  style={{ borderColor: '#D6D0C2', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
                  Confirm New Password
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  style={{ borderColor: '#D6D0C2', fontFamily: "'Bricolage Grotesque', sans-serif" }}
                />
              </div>
              <Button
                type="submit"
                style={{ 
                  backgroundColor: '#51120A', 
                  color: 'white', 
                  fontFamily: "'Bricolage Grotesque', sans-serif" 
                }}
              >
                Update Password
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Cancel Account */}
        <Card className="mt-6" style={{ backgroundColor: '#F3EEE4', border: '1px solid #D6D0C2', boxShadow: 'none' }}>
          <CardHeader>
            <CardTitle style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
              Cancel Account
            </CardTitle>
            <CardDescription style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              Permanently delete your account and profile. Your recipes will remain but will no longer be linked to an account. This action cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showCancelDialog ? (
              <Button
                variant="outline"
                onClick={() => setShowCancelDialog(true)}
                style={{ 
                  borderColor: '#51120A',
                  color: '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif"
                }}
              >
                Cancel My Account
              </Button>
            ) : (
              <div className="space-y-4">
                <p style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A', opacity: 0.8, fontSize: '0.9rem' }}>
                  This will permanently delete your account and profile. Any active subscription will also be cancelled. Your recipes will remain but will no longer be linked to an account.
                </p>
                <div>
                  <Label style={{ fontFamily: "'Bricolage Grotesque', sans-serif", color: '#51120A' }}>
                    Type <span style={{ fontWeight: 600 }}>CANCEL</span> to confirm
                  </Label>
                  <Input
                    value={cancelConfirmText}
                    onChange={(e) => setCancelConfirmText(e.target.value)}
                    placeholder="CANCEL"
                    className="mt-1"
                    style={{ 
                      borderColor: '#D6D0C2', 
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      backgroundColor: 'transparent'
                    }}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => { setShowCancelDialog(false); setCancelConfirmText(''); }}
                    style={{ fontFamily: "'Bricolage Grotesque', sans-serif", borderColor: '#D6D0C2' }}
                  >
                    Go Back
                  </Button>
                  <Button
                    onClick={handleCancelAccount}
                    disabled={cancelConfirmText !== 'CANCEL' || isCancelling}
                    style={{ 
                      backgroundColor: cancelConfirmText === 'CANCEL' ? '#51120A' : '#D6D0C2',
                      color: cancelConfirmText === 'CANCEL' ? 'white' : '#999',
                      fontFamily: "'Bricolage Grotesque', sans-serif"
                    }}
                  >
                    {isCancelling ? 'Cancelling...' : 'Cancel My Account'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Account;
